import React from 'react';
import { type SetURLSearchParams, useSearchParams } from 'react-router-dom';

// Drop-in replacement for useSearchParams that also mirrors the search params to sessionStorage,
// so filters survive sidebar navigation (NavLinks to bare paths wipe the query string, while
// browser back already preserves the URL — explicit URLs always win over the saved value).
//
// On first render: if the URL is empty and a value was saved, the hook returns the saved params
// synchronously (so SWR fetches the right data on the first render, no flicker) and pushes them
// into the URL via a post-commit effect. After hydration completes, the live URL is the only
// source of truth — clearing filters writes the empty querystring back to sessionStorage so the
// next visit doesn't resurrect them.
export const useSessionPersistedFilters = (storageKey: string, enabled = true): readonly [URLSearchParams, SetURLSearchParams] => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Computed once at mount. Holds the saved params if (and only if) the URL was empty and a value was saved.
  // biome-ignore lint/correctness/useExhaustiveDependencies: only the mount value matters
  const hydratedParams = React.useMemo<URLSearchParams | null>(() => {
    if (!enabled || searchParams.toString() !== '') return null;
    const saved = window.sessionStorage.getItem(storageKey);
    return saved ? new URLSearchParams(saved) : null;
  }, []);

  const [hydrated, setHydrated] = React.useState(false);

  // Push hydrated params into the URL after the first commit so React Router stays in sync.
  // biome-ignore lint/correctness/useExhaustiveDependencies: run once on mount
  React.useEffect(() => {
    if (hydratedParams) setSearchParams(hydratedParams.toString(), { replace: true });
    setHydrated(true);
  }, []);

  // Save the current querystring on every change — skip the pre-hydration window so we don't
  // overwrite the saved value with the empty URL before our hydration effect lands.
  React.useEffect(() => {
    if (!enabled || !hydrated) return;
    window.sessionStorage.setItem(storageKey, searchParams.toString());
  }, [searchParams, storageKey, enabled, hydrated]);

  const effective = !hydrated && hydratedParams ? hydratedParams : searchParams;
  return [effective, setSearchParams] as const;
};
